import { toPcm16k } from './micWavCapture.js';

/**
 * La escucha EN VIVO de una toma del repaso de correcciones (`/ws/review-live`,
 * Emmanuel, 15/10: «que te vaya resaltando como en karaoke las palabras que vas
 * diciendo; sin mirar, que vaya llenando los vacíos»).
 *
 * El mismo audio de la toma sale mientras habla (PCM 16 kHz de 16 bits, en
 * binario) y vuelve lo que Scribe realtime va oyendo. Sólo para mostrar: la toma
 * entera sigue yendo a `review-check`, que es la que decide. Si esta conexión no
 * anda, el repaso sigue igual, sin las palabras en vivo (queda un aviso en la
 * consola, que en dev llega al log del server).
 */
export interface ReviewLive {
  /** Un pedazo de la toma, tal como lo da el micrófono. */
  push: (samples: Float32Array, sampleRate: number) => void;
  /** Terminó la toma: Scribe cierra el final. */
  end: () => void;
  close: () => void;
}

export function openReviewLive(handlers: {
  /** Lo que va oyendo (`final` = lo último, después de `end`). */
  onText: (text: string, final: boolean) => void;
}): ReviewLive {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const ws = new WebSocket(`${proto}//${window.location.host}/ws/review-live`);
  ws.binaryType = 'arraybuffer';
  const queue: ArrayBuffer[] = [];
  let ended = false;
  ws.onopen = () => {
    for (const pcm of queue.splice(0)) ws.send(pcm);
    if (ended) ws.send(JSON.stringify({ type: 'end' }));
  };
  ws.onmessage = (e: MessageEvent) => {
    let msg: { type?: unknown; text?: unknown };
    try {
      msg = JSON.parse(String(e.data)) as typeof msg;
    } catch {
      return;
    }
    if ((msg.type === 'partial' || msg.type === 'final') && typeof msg.text === 'string') {
      handlers.onText(msg.text, msg.type === 'final');
    }
  };
  ws.onclose = (e: CloseEvent) => {
    if (e.code !== 1000 && e.code !== 1005) {
      console.warn('[review-live] cerrada', e.code, e.reason);
    }
  };
  ws.onerror = () => console.warn('[review-live] error de conexión');
  return {
    push(samples, sampleRate) {
      if (ended) return;
      const pcm = toPcm16k(samples, sampleRate);
      if (ws.readyState === WebSocket.OPEN) ws.send(pcm);
      else if (ws.readyState === WebSocket.CONNECTING) queue.push(pcm);
    },
    end() {
      if (ended) return;
      ended = true;
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'end' }));
    },
    close() {
      ws.close();
    },
  };
}
